import { defineStore } from 'pinia'
import { useCustomersStore } from '@/store/pinia/customers'
import { useOrganizationStore } from '@/store/pinia/organization'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction } from '@/utils/helpers/pinia'

export const state = () => ({
  detail: {},
  isFetched: false,
  errors: [],
})

export const useLicenceStore = defineStore('licence', {
  state,
  getters: {
    hasLicence: (state) => !_.$isEmpty(state.detail),
    isExpired: (state) => {
      return state.detail
        ? state.detail.expires_at <= Date.now()
        : null
    },
    isOnPrem: (state) => !!state.detail?.on_prem,
    isMaxMembersReached: (state) => {
      if (state.membersLeft === null) {
        return null
      }
      return state.membersLeft <= 0
    },
    membersLeft: (state) => {
      if (!state.detail) return null

      const { current_members: membersCount = null, members_count: maxMembers = null } = state.detail
      if (_.some([membersCount, maxMembers], _.isNull)) return null

      return maxMembers - membersCount
    },
    membersLeftPercentage: (state) => {
      if (!state.detail) return null

      const { current_members: membersCount = null, members_count: maxMembers = null } = state.detail
      if (_.some([membersCount, maxMembers], _.isNull)) return null

      return Math.floor((maxMembers - membersCount) * 100 / maxMembers)
    },
  },
  actions: {
    /**
     * Clear the current errors list.
     */
    CLEAR_LICENCE_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_LICENCE_STATE: makeResetStoreAction(state),

    /**
     * Replace the current errors array.
     */
    SET_LICENCE_ERRORS: makeSetErrorsAction(),

    /**
     * Set licence detail in state.
     * @param {Object} detail
     * @returns {void}
     */
    SET_LICENCE (detail) {
      this.detail = detail
    },

    /**
     * Set fetched status in state.
     * @param {boolean} isFetched
     * @returns {void}
     */
    SET_FETCHED (isFetched) {
      this.isFetched = isFetched
    },

    /**
     * Get licence information.
     * @returns {Promise<void>}
     */
    async GET_LICENCE () {
      const orgStore = useOrganizationStore()

      this.CLEAR_LICENCE_ERRORS()

      if (_.$isEmpty(orgStore.orgCanonical)) return

      const { data, errors } = (await this.$cycloid.ydAPI.getLicence(orgStore.orgCanonical)) || {}

      if (data) {
        this.SET_LICENCE(data)
        this.SET_FETCHED(true)
      }
      if (errors) this.SET_LICENCE_ERRORS({ errors })
    },

    /**
     * Activate licence with token.
     * @param {Object} params
     * @param {string} params.licenceToken
     * @returns {Promise<void>}
     */
    async ACTIVATE_LICENCE ({ licenceToken }) {
      const orgStore = useOrganizationStore()
      const customersStore = useCustomersStore()

      if (_.$isEmpty(licenceToken) || customersStore.currentScopeDepth !== 0) return

      this.CLEAR_LICENCE_ERRORS()

      const { errors } = (await this.$cycloid.ydAPI.activateLicence(orgStore.orgCanonical, { key: licenceToken })) || {}

      if (!_.$isEmpty(errors)) this.SET_LICENCE_ERRORS({ errors })
      else await this.GET_LICENCE()
    },
  },
})
